import { Component, Inject, OnInit } from '@angular/core';
import { APP_VERSION, ClockService, Complaint, ComplaintSparePart, ProductsList, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import { ProductsService } from 'libs/b2b/trade/src/lib/products.service';
import { LoaderService } from '@icc/b2b/sales-document';
import { HttpClient } from '@angular/common/http';
import { ConfigService, core, PricesDataService } from '@icc/shared';

@Component({
    selector: 'icc-complaint-spare-parts-dialog',
    templateUrl: './complaint-spare-parts-dialog.component.html',
    styleUrls: ['./complaint-spare-parts-dialog.component.scss'],
})
export class ComplaintSparePartsDialogComponent implements OnInit {
    productId = 0;
    productsList!: ProductsList;
    allProductsList!: ProductsList;

    complaintSparePartsStocks: any[] = [];
    invalidSpareParts = false;

    complaintSpareParts: (ComplaintSparePart & {
        invalidQuantity?: boolean;
        maxQuantity?: number;
    })[] = [];

    constructor(
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            complaint: Complaint;
        },
        private productsService: ProductsService,
        private loaderService: LoaderService,
        private http: HttpClient,
        private configService: ConfigService,
        @Inject(APP_VERSION) private appVersion: string,
        private pricesDataService: PricesDataService,
    ) {}

    async ngOnInit() {
        await this.loadData();
        await this.getRemoteComplaintSparePartsStocks();
        await this.updateProductList();
    }

    private async loadData() {
        await this.productsService.loadData().then(() =>
            this.productsService.getData().then((productData) => {
                this.allProductsList = productData.productsList;
            })
        );
    }

    handleSparePart($event) {
        this.productId = $event;
    }

    getProductName(productId: number) {
        return this.allProductsList.find(product => product.id === productId)?.name;
    }

    getUnits(productId: number) {
        const priceUnits = this.allProductsList.find(product => product.id === productId)?.price.priceUnits;
        const units: { id: number; code: string; name: string }[] = [];
        for (const unit of priceUnits || []) {
            const unitName = this.pricesDataService.getUnitInfo(unit.id, unit.code)?.name || '';
            units.push({
                id: unit.id,
                code: unit.code,
                name: unitName,
            });
        }
        return units;
    }

    handleUnit($event: string, sparePart: ComplaintSparePart) {
        const [unitId, unitCode] = $event.split(':');
        sparePart.unitId = Number(unitId) ?? 0;
        sparePart.unitCode = unitCode ?? '';
    }

    addSparePartToComplaint() {
        const product = this.allProductsList.find(prod => prod.id === this.productId);
        if (product) {
            const stock = this.complaintSparePartsStocks.find(el => el.productId === product.id);
            if (!stock?.stock || stock.stock < 1) {
                return;
            }
            const unitId = this.complaintSparePartsStocks.find(el => el.productId === product.id)?.unitId ?? product.price.priceUnits[0].id ?? 0;
            const unitCode = this.complaintSparePartsStocks.find(el => el.productId === product.id)?.unitCode ?? product.price.priceUnits[0].code ?? '';
            const complaintSparePart = {
                uuid: core.generateUUID(),
                productId: this.productId,
                quantity: 1,
                unitId,
                unitCode,
                documentId: this.data.complaint.uuid,
                machineId: ClockService.getClock().timestamp.node(),
                companyId: Number(this.configService?.user?.user?.ActiveCompany),
                maxQuantity: stock.stock,
            };
            this.complaintSpareParts.push(complaintSparePart);
            this.productId = 0;
            this.updateProductList();
        }
    }

    async getRemoteComplaintSparePartsStocks() {
        this.loaderService.setLoadingAfterTime();
        const serverComplaintSparePartsStocks = await this.http.post<{
            complaintSpareParts: any[],
        }>(`${window.location.origin}/api/b2b/spare-parts-stock`, {
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
        }).toPromise();
        if (serverComplaintSparePartsStocks?.complaintSpareParts) {
            this.complaintSparePartsStocks = serverComplaintSparePartsStocks.complaintSpareParts;
        }
        this.loaderService.setLoaded();
    }

    updateProductList() {
        this.productsList = this.allProductsList.filter(product =>
            this.complaintSparePartsStocks.some(stock => stock.productId === product.id && stock.stock > 0)
        );
        this.productsList = this.productsList.filter(product => !this.complaintSpareParts.some(part => part.productId === product.id));
    }

    removeSparePartFromList(complaintSparePart: ComplaintSparePart) {
        this.complaintSpareParts = this.complaintSpareParts.filter(part => part.uuid !== complaintSparePart.uuid);
        this.updateProductList();
    }

    changedQuantity(complaintSparePart: ComplaintSparePart & { invalidQuantity?: boolean }) {
        const stock = this.complaintSparePartsStocks.find(el => el.productId === complaintSparePart.productId);
        if (stock) {
            complaintSparePart.invalidQuantity = complaintSparePart.quantity > stock.stock;
            this.invalidSpareParts = this.complaintSpareParts.some(part => part.invalidQuantity);
        }
    }

    async save() {
        await this.getRemoteComplaintSparePartsStocks();
        this.complaintSpareParts.forEach(part => {
            const stock = this.complaintSparePartsStocks.find(el => el.productId === part.productId);
            part.invalidQuantity = stock && part.quantity > stock.stock;
        });
        const validParts = this.complaintSpareParts.every(part => !part.invalidQuantity);
        if (validParts) {
            this.complaintSpareParts.forEach(part => {
                delete part.invalidQuantity;
                delete part.maxQuantity;
            });
            this.dialogRef.close(this.complaintSpareParts);
            this.invalidSpareParts = false;
        } else {
            this.invalidSpareParts = true;
        }
    }

    close() {
        this.dialogRef.close();
    }
}
